import { getInterestIcon } from "@/components/mentor-profile/interest-icons";
import { cn } from "@/lib/utils";
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";

const CARD_ASPECT = "223 / 299";
const PEEK_OFFSETS_PX = [10, 18] as const;
const SWIPE_THRESHOLD_PX = 48;

type InterestStackImage = {
  interest: string;
  imageUrl: string;
  signedUrl?: string;
};

function cardKey(slot: string, image?: InterestStackImage) {
  if (!image) return `${slot}:empty`;
  return `${slot}:${image.interest}:${image.imageUrl}`;
}

function InterestStackCard({
  image,
  className,
  style,
}: {
  image?: InterestStackImage;
  className?: string;
  style?: CSSProperties;
}) {
  const Icon = image ? getInterestIcon(image.interest) : null;

  return (
    <div
      className={cn(
        "absolute inset-x-0 top-0 overflow-hidden rounded-[22px] border-4 border-white bg-[#f1ece3] shadow-[0_10px_27px_rgba(31,30,29,0.18)]",
        className,
      )}
      style={{ aspectRatio: CARD_ASPECT, ...style }}
    >
      {image?.signedUrl ? (
        <img
          src={image.signedUrl}
          alt=""
          className="pointer-events-none size-full object-cover"
          draggable={false}
        />
      ) : null}
      {image ? (
        <div className="pointer-events-none absolute left-2 top-2 inline-flex items-center gap-1.5 rounded-full border border-white/60 bg-black/35 px-2.5 py-1">
          {Icon ? (
            <Icon className="size-3.5 shrink-0 text-white" strokeWidth={1.75} />
          ) : null}
          <span className="font-body text-xs font-medium leading-4 text-white">
            {image.interest}
          </span>
        </div>
      ) : null}
    </div>
  );
}

export function InterestPhotoStack({
  images,
}: {
  images: InterestStackImage[];
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const pointerIdRef = useRef<number | null>(null);
  const startXRef = useRef(0);
  const imageKey = images.map((image) => image.imageUrl).join("|");

  useEffect(() => {
    setActiveIndex(0);
    setDragX(0);
  }, [imageKey]);

  const count = images.length;
  const canSwipe = count > 1;
  const front = count > 0 ? images[activeIndex % count] : undefined;
  const peekLayers = PEEK_OFFSETS_PX.map((offset, depth) => ({
    offset,
    image:
      count > depth + 1
        ? images[(activeIndex + depth + 1) % count]
        : undefined,
  }));

  const advance = (direction: 1 | -1) => {
    if (!canSwipe) return;
    setActiveIndex((index) => (index + direction + count) % count);
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!canSwipe) return;
    pointerIdRef.current = event.pointerId;
    startXRef.current = event.clientX;
    setIsDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!isDragging || pointerIdRef.current !== event.pointerId) return;
    setDragX(event.clientX - startXRef.current);
  };

  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!isDragging || pointerIdRef.current !== event.pointerId) return;
    const delta = event.clientX - startXRef.current;
    if (Math.abs(delta) >= SWIPE_THRESHOLD_PX) {
      advance(delta < 0 ? 1 : -1);
    }
    setDragX(0);
    setIsDragging(false);
    pointerIdRef.current = null;
  };

  return (
    <div
      className="relative mx-auto mt-1.5 w-full max-w-[224px] select-none"
      style={{ paddingBottom: PEEK_OFFSETS_PX[1] }}
    >
      <div className="relative w-full" style={{ aspectRatio: CARD_ASPECT }}>
        {[...peekLayers].reverse().map((layer, index) => (
          <InterestStackCard
            key={cardKey(`peek-${layer.offset}`, layer.image)}
            image={layer.image}
            className="pointer-events-none"
            style={{
              zIndex: index,
              top: layer.offset,
              transform: "scale(0.97)",
              transformOrigin: "bottom center",
              boxShadow: "none",
            }}
          />
        ))}

        <InterestStackCard
          key={cardKey("front", front)}
          image={front}
          className={cn(
            "z-10",
            canSwipe && "cursor-grab active:cursor-grabbing",
            !isDragging && "transition-transform duration-300 ease-out",
          )}
          style={{
            transform: `translateX(${dragX}px) rotate(${dragX / 28}deg)`,
          }}
        />

        {canSwipe ? (
          <div
            className="absolute inset-0 z-20 touch-none"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            role="group"
            aria-label="Interest photos. Swipe to see the next one."
          />
        ) : null}
      </div>
    </div>
  );
}
